import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import resolveAiScreenContext from 'utils/resolveAiScreenContext';

import type IAiScreenContext from 'utils/interfaces/IAiScreenContext';
import type { IAiSubScreenContext, TAiScreenContextInput } from 'utils/interfaces/IAiScreenContext';
import type IAiScreenContextProvider from './interfaces/IAiScreenContextProvider';

/**
 * What a component declares itself as. A screen replaces the one declared before it; a sub-screen
 * stacks on the screen, and on the sub-screens opened before it.
 */
type TAiScreenEntry =
  | { id: string, order: number, kind: 'screen', context: TAiScreenContextInput }
  | { id: string, order: number, kind: 'subScreen', context: IAiSubScreenContext };

interface IAiScreenContextRegistry {
  register: (entry: TAiScreenEntry) => void,
  unregister: (id: string) => void,
}

const NO_ENTRIES: TAiScreenEntry[] = [];

/**
 * Where the next component to declare itself goes in the stack.
 *
 * Taken during render, which React runs parent-first, rather than in the effect that registers,
 * which it runs child-first: a modal rendered inside another would otherwise register before it
 * and be read as under it. A component opened later takes a later number either way.
 */
let nextOrder = 0;

const takeOrder = (): number => {
  nextOrder += 1;

  return nextOrder;
};

// Split in two on purpose: registering is a stable pair of callbacks, while the entries change
// whenever a screen mounts. A screen that only declares context is not re-rendered by the change.
const AiScreenRegistryContext = createContext<IAiScreenContextRegistry>({
  register: () => {},
  unregister: () => {},
});

const AiScreenEntriesContext = createContext<TAiScreenEntry[]>(NO_ENTRIES);

/**
 * Holds what each mounted screen and sub-screen has declared about itself.
 *
 * Entries are kept in the order they took: outer before inner, earlier before later. For screens
 * that makes the last one the one the assistant reads; for sub-screens it makes the entries a
 * stack, the way the modals they describe are opened one over another.
 *
 * Read the spec before you change how entries are resolved:
 * @see [9.2 Screen context (frontend)](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
function AiScreenContextProvider({ children }: IAiScreenContextProvider) {
  const [entries, setEntries] = useState<TAiScreenEntry[]>(NO_ENTRIES);

  const register = useCallback((entry: TAiScreenEntry) => {
    // A screen that re-declares itself keeps its number, so it keeps its place in the stack.
    setEntries((current) => [...current.filter((other) => other.id !== entry.id), entry]
      .sort((a, b) => a.order - b.order));
  }, []);

  const unregister = useCallback((id: string) => {
    setEntries((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const registry = useMemo<IAiScreenContextRegistry>(
    () => ({ register, unregister }),
    [register, unregister],
  );

  return (
    <AiScreenRegistryContext.Provider value={registry}>
      <AiScreenEntriesContext.Provider value={entries}>
        {children}
      </AiScreenEntriesContext.Provider>
    </AiScreenRegistryContext.Provider>
  );
}

/** Keeps one entry registered for as long as the caller is mounted. */
function useAiScreenEntry(entry: Omit<TAiScreenEntry, 'id' | 'order'>): void {
  const id = useId();
  const [order] = useState<number>(takeOrder);
  const { register, unregister } = useContext(AiScreenRegistryContext);
  // Compared by value, so a descriptor written inline — which is how most read best — does not
  // register again on every render of the screen.
  const serialized = JSON.stringify(entry);

  useEffect(() => {
    register({ id, order, ...JSON.parse(serialized) } as TAiScreenEntry);

    return () => unregister(id);
  }, [id, order, register, serialized, unregister]);
}

/**
 * Declares what the assistant should know about this screen, on top of what its route says.
 *
 * This is the default way to declare context: whatever calls it is read as the screen the user is
 * on. The registration is dropped when the screen unmounts, which leaves the route floor behind.
 *
 * @see [12.5 Register a screen on the frontend](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export function useAiScreenContext(context: TAiScreenContextInput): void {
  useAiScreenEntry({ kind: 'screen', context });
}

/**
 * Declares a sub-screen open over the screen: a modal, a drawer, a step of a wizard.
 *
 * The screen keeps its own context; the sub-screen is added on top, after any opened before it,
 * and is taken off the stack when it unmounts. A module opts into this on purpose — a component
 * that does not call it is read as part of the screen.
 *
 * @see [12.6 Declare a sub-screen](../../docs/mockups/ai-agents/ai-chat/ATT-144_AI_Chat_Assistant.md)
 */
export function useAiSubScreenContext(context: IAiSubScreenContext): void {
  useAiScreenEntry({ kind: 'subScreen', context });
}

/**
 * What the assistant reads: the route floor, enriched by the screen that registered last, with
 * the open sub-screens stacked on it.
 */
export function useResolvedAiScreenContext(): IAiScreenContext {
  const { pathname, search } = useLocation();
  const entries = useContext(AiScreenEntriesContext);

  return useMemo(() => {
    const screen = entries.reduce<TAiScreenContextInput | null>(
      (current, entry) => (entry.kind === 'screen' ? entry.context : current),
      null,
    );
    const subScreens = entries.flatMap((entry) => (entry.kind === 'subScreen' ? [entry.context] : []));

    return resolveAiScreenContext({ pathname, search }, screen, subScreens);
  }, [entries, pathname, search]);
}

export default AiScreenContextProvider;
